import React from "react";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary, {
  accordionSummaryClasses,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import WorkingDocuments from "./working-documents/WorkingDocuments";
// import { useTools } from "../../../redux/hooks/useTools";
// import { useWorkingDocument } from "../../../redux/hooks/useWorkingDocument";
// import { useOneDriveDocsList } from "../../../redux/hooks/useOneDriveDocsList";
import DocumentsContent from "./all-documents/DocumentsContent";
import CheckInNewListContent from "../../check-in-new-list/CheckInNewListContent";
import { useTheme } from "../../../theme/OAS365Theme";
// import RefreshTwoTone from "@mui/icons-material/RefreshTwoTone";
// import IconButton from "@mui/material/IconButton";
import {
  DOCUMENT_LOAD_STATUS,
  PROGRESS_STATUS,
} from "../../../redux/constants/metaData";
import { useChannel } from "../../../redux/hooks/useChannel";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getRouteByDocumentPanel,
  getDocumentPanelFromPath,
  getCheckInDocumentsNavLabel,
  getDocumentPanelInfoTooltip,
  DOCUMENT_TAB_PANEL,
  NAV_LOCATION_STATE,
} from "../../../routes/routeConfig";
import { useLogin } from "../../../redux/hooks/useLogin";
import { useUploadToOneDrive } from "../../../redux/hooks/useUploadToOneDrive";
import { useTools } from "../../../redux/hooks/useTools";
import { useTemplatesPanel } from "../../../redux/hooks/useTemplatesPanel";
import { useWorkingDocument } from "../../../redux/hooks/useWorkingDocument";
import { useOneDriveDocsList } from "../../../redux/hooks/useOneDriveDocsList";
import { useTeamsRequestContext } from "../../../redux/hooks/useTeamsRequestContext";
import { useAppDispatch } from "../../../redux/hooks";
import {
  ON_ERROR_MESSAGE_INVOKE,
  ON_SUCCESS_MESSAGE_INVOKE,
} from "../../../redux/constants/actionTypes";
import {
  ERROR_TYPE,
  SUCCESS_TYPE,
  OVERWRITE_DOCUMENT_CONFIRMATION_TITLE,
  OVERWRITE_TEMPLATE_CONFIRMATION_MSG,
} from "../../../redux/constants/messageTypes";
import ConfirmModal from "../../../components/popupModals/ConfirmModal";
import AccordionHeaderLabel from "./AccordionHeaderLabel";
import UserAuth from "../../../auth/userAuth";
import {
  getLanguageCode,
  getTemplatesByFileTypeAndLanguageCode,
  getTemplatesDownload,
} from "../../../redux/api/documentsAPI";
import LanguageSelectionDropdown, {
  TEMPLATE_DEFAULT_LANGUAGE_OPTION,
  TEMPLATE_LANGUAGE_ALL_OPTION,
} from "./templates/LanguageSelectionDropdown";
import TemplatesList from "./templates/TemplatesList";
import { ReusableDialog } from "../../../components/popupModals/ReusableDialog";
import DocumentDetailsLoadingContent from "../../../components/moreDetails/DocumentDetailsLoadingContent";
import {
  getLanguageCodesFromList,
  isAllLanguagesSelection,
  mergeTemplatesUnique,
  normalizeTemplatesApiData,
  shouldAlwaysRefreshTemplatesSelection,
} from "./templates/templateLanguageUtils";
import { isTemplateInOneDrive } from "./templates/templateOneDriveUtils";
import { openDocumentPreferDesktop } from "../../../utils/openDocumentPreferDesktop";

const userAuth = new UserAuth();

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderLeft: 0,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: "rotate(90deg)",
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  ...theme.applyStyles("dark", {
    backgroundColor: "rgba(255, 255, 255, .05)",
  }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

function isAccordionExpandIconClick(event) {
  return Boolean(
    event?.target?.closest?.(`.${accordionSummaryClasses.expandIconWrapper}`)
  );
}

/**
 * Header refresh control (hidden): uncomment imports above, the block below, hooks/handlers in
 * IfsDocsAccordionContent, and the two <AccordionHeaderRefreshButton /> usages to restore.
 */
/*
function AccordionHeaderRefreshButton({
  theme,
  onClick,
  disabled,
  title = "Refresh",
  isDarkMode = false,
  isSelected = false,
}) {
  const iconColor = isDarkMode && isSelected ? "#ffffff" : theme.colors.addovation70Green;
  return (
    <IconButton
      size="small"
      onClick={onClick}
      disabled={disabled}
      aria-label={title}
      title={title}
      sx={{
        flexShrink: 0,
        ml: 1,
        border: "none",
        borderRadius: "8px",
        width: 36,
        height: 36,
        boxSizing: "border-box",
        padding: 0,
        backgroundColor: "transparent",
        color: iconColor,
        "&:hover": {
          backgroundColor: "transparent",
          color: iconColor,
        },
        "&.Mui-disabled": {
          opacity: 0.55,
        },
      }}
    >
      <RefreshTwoTone
        sx={{
          fontSize: "1.35rem",
          color: iconColor,
          "& .MuiSvgIcon-colorSecondary": {
            color: "#f5a623",
            opacity: 1,
          },
        }}
      />
    </IconButton>
  );
}
*/

/** Expanded accordion fills remaining viewport; collapsed headers stay fixed height. */
const expandedAccordionLayoutSx = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  "&::before": { display: "none" },
  "& .MuiAccordion-region": {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  "& .MuiCollapse-root": {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  "& .MuiCollapse-wrapper": {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  "& .MuiCollapse-wrapperInner": {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  "& .MuiAccordionDetails-root": {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
};

function buildAccordionShellSx(isExpanded, theme, padding = "0.25em") {
  return {
    background: theme.colors.pureColor,
    padding,
    border: "0px solid black",
    flexShrink: 0,
    ...(isExpanded ? expandedAccordionLayoutSx : {}),
  };
}

function buildAccordionPanelDetailsStyle(background, extra = {}) {
  return {
    margin: "5px 0 0 0",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    background,
    borderRadius: "10px",
    border: "0px solid black",
    ...extra,
  };
}

function normalizeTemplateExtension(fileExtension) {
  const ext = String(fileExtension ?? "").trim();
  if (!ext) return "";
  return ext.startsWith(".") ? ext.slice(1) : ext;
}

/** Extension (without the dot) parsed from a file name, or "" if none. */
function getFileNameExtension(fileName) {
  const name = String(fileName ?? "").trim();
  const lastDot = name.lastIndexOf(".");
  return lastDot > 0 ? name.slice(lastDot + 1) : "";
}

function parseFileNameFromContentDisposition(contentDispositionHeader) {
  const contentDisposition = String(contentDispositionHeader ?? "");
  if (!contentDisposition) return "";

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const standardMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return standardMatch?.[1] ?? "";
}

function decodeTemplateDownloadResponse(data, fallbackMimeType) {
  if (data instanceof Blob) {
    return data;
  }

  if (data instanceof ArrayBuffer) {
    return new Blob([data], { type: fallbackMimeType || "application/octet-stream" });
  }

  const base64FromObject =
    data?.fileBase64 ??
    data?.FileBase64 ??
    data?.base64 ??
    data?.Base64 ??
    "";

  const base64FromString = typeof data === "string" ? data : "";
  const raw = String(base64FromObject || base64FromString || "").trim();
  if (!raw) return null;

  const dataUrlMatch = raw.match(/^data:([^;]+);base64,(.+)$/i);
  const mimeType = dataUrlMatch?.[1] || fallbackMimeType || "application/octet-stream";
  const base64Payload = dataUrlMatch?.[2] || raw;
  const binary = window.atob(base64Payload);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

const IfsDocsAccordionContent = ({ Title, onDocTitleChange, onRefreshWorkingDocuments }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isDarkMode = String(theme.colors.pureColor || "").toLowerCase() === "#000000";
  const workingDocumentsPanelBackground = isDarkMode
    ? theme.colors.themeColorBackground
    : theme.colors.pureColor;
  // const { onWorkingDocumentListStatusChange } = useTools();
  // const { refreshWorkingDocuments, isListLoading } = useWorkingDocument();
  // const { requestRefreshCheckInNewList, loading: checkInNewListLoading } = useOneDriveDocsList();
  const {
    channelConfigLoad,
    channelObjectList,
    channelConnectionWarningDismissed,
    isPrimaryChannel = false,
  } = useChannel();
  const navigate = useNavigate();
  const location = useLocation();
  const { teamsContext } = useLogin();
  const {
    uploadTemplateToOneDrive,
    closeDownloadSuccessDialog,
    state: uploadState,
  } = useUploadToOneDrive();
  const {
    onWorkingDocumentListStatusChange,
    onRequestRefreshAllDocuments,
    onRequestRefreshWorkingDocuments,
    onRequestRefreshTemplates,
  } = useTools();
  const { refreshTrigger: templatesRefreshTrigger } = useTemplatesPanel();
  const { workingDocumentList, onWorkingDocumentLoad } = useWorkingDocument();
  const [showTemplateDownloadConfirm, setShowTemplateDownloadConfirm] = React.useState(false);
  const [templateToDownload, setTemplateToDownload] = React.useState(null);
  const [isTemplateLocalDownloadInProgress, setIsTemplateLocalDownloadInProgress] =
    React.useState(false);
  const { requestRefreshCheckInNewList } = useOneDriveDocsList();
  const {
    tenantId: requestTenantId,
    groupId: requestGroupId,
    channelId: requestChannelId,
  } = useTeamsRequestContext();
  const checkInDocumentsNavLabel = getCheckInDocumentsNavLabel(teamsContext);

  const isGeneralAppContext = !teamsContext?.channel?.id;
  const effectiveChannelId = isGeneralAppContext
    ? ""
    : teamsContext?.channel?.id ?? requestChannelId ?? "";
  const effectiveGroupId = isGeneralAppContext
    ? ""
    : teamsContext?.channel?.membershipType === "Private"
      ? teamsContext?.channel?.ownerGroupId ?? requestGroupId ?? ""
      : teamsContext?.team?.groupId ?? requestGroupId ?? "";
  const effectiveFileRoot = effectiveChannelId ? "/ChannelDoc" : "/MainMenu";

  // Build connected objects summary for tooltip
  const connectedObjectsTooltipLines = React.useMemo(() => {
    if (!channelObjectList || channelObjectList.length === 0) return null;

    const lines = channelObjectList
      .map((obj) => {
        const type = obj.nameDesc || obj.luName || "";

        // Backend provides "docObjDesc"; when available, use it directly as the key part.
        if (obj?.docObjDesc) {
          const keyVal = String(obj.docObjDesc);
          return type && keyVal ? `${type}:${keyVal}` : type || keyVal;
        }

        const key = obj.keyRef || obj.keyDesc || "";

        let keyVal = key;
        if (key.includes("=") && key.includes("^")) {
          const segments = key.split("^").map((s) => s.trim()).filter(Boolean);
          const values = segments.map((seg) => {
            const parts = seg.split("=");
            return parts.length > 1 ? parts.slice(1).join("=") : seg;
          });
          keyVal = values.join(", ");
        } else {
          keyVal = key.replace(/\^/g, "");
        }

        return type && keyVal ? `${type}:${keyVal}` : type || keyVal;
      })
      .filter(Boolean);

    if (!lines || lines.length === 0) return null;

    // Return the label and the objects as separate elements so they appear on different lines.
    return [
  <>
    Connected Object(s):
    <br />
    {lines.join(" , ")}
  </>,
];
  }, [channelObjectList]);

  const membershipType = teamsContext?.channel?.membershipType;
  const isUnsupportedChannel = false;
  const showChannelConnectionWarning =
    isUnsupportedChannel ||
    ((channelConfigLoad === PROGRESS_STATUS.onCompletion ||
      channelConfigLoad === PROGRESS_STATUS.noData) &&
    (!channelObjectList || channelObjectList.length === 0) &&
    !channelConnectionWarningDismissed);
  // Accordions remain disabled while there are no object connections,
  // even if the warning message has been dismissed
  const disableDocumentAccordions =
    isUnsupportedChannel ||
    ((channelConfigLoad === PROGRESS_STATUS.onCompletion ||
      channelConfigLoad === PROGRESS_STATUS.noData) &&
    (!channelObjectList || channelObjectList.length === 0));

  const panelFromUrl = getDocumentPanelFromPath(location.pathname);
  const [expandedPanel, setExpandedPanel] = React.useState(
    () => panelFromUrl || DOCUMENT_TAB_PANEL.workingDocuments
  );
  /** Consumed navigation keys for arrow-open route changes (avoids Strict Mode double refresh). */
  const consumedSkipRouteRefreshKeyRef = React.useRef(null);
  // Mount once on first open so arrow close/reopen does not remount and refetch (All Documents).
  const [hasOpenedAllDocuments, setHasOpenedAllDocuments] = React.useState(
    () => panelFromUrl === DOCUMENT_TAB_PANEL.allDocuments
  );
  const [hasOpenedCheckInNew, setHasOpenedCheckInNew] = React.useState(
    () => panelFromUrl === DOCUMENT_TAB_PANEL.checkInNew
  );

  React.useEffect(() => {
    if (expandedPanel === DOCUMENT_TAB_PANEL.allDocuments) {
      setHasOpenedAllDocuments(true);
    }
    if (expandedPanel === DOCUMENT_TAB_PANEL.checkInNew) {
      setHasOpenedCheckInNew(true);
    }
  }, [expandedPanel]);

  // Keep local state in sync with URL (e.g. when sidebar is clicked)
  React.useEffect(() => {
    const panel = getDocumentPanelFromPath(location.pathname);
    if (panel && !disableDocumentAccordions) setExpandedPanel(panel);
  }, [location.pathname, disableDocumentAccordions]);

  // Refresh lists when route changes (sidebar or accordion header navigation).
  React.useEffect(() => {
    const panel = getDocumentPanelFromPath(location.pathname);
    if (!panel || disableDocumentAccordions) return;

    if (location.state?.[NAV_LOCATION_STATE.skipDocumentPanelRouteRefresh]) {
      if (consumedSkipRouteRefreshKeyRef.current !== location.key) {
        consumedSkipRouteRefreshKeyRef.current = location.key;
        return;
      }
      return;
    }
    consumedSkipRouteRefreshKeyRef.current = null;

    switch (panel) {
      case DOCUMENT_TAB_PANEL.workingDocuments:
        onRequestRefreshWorkingDocuments();
        break;
      case DOCUMENT_TAB_PANEL.allDocuments:
        onRequestRefreshAllDocuments();
        onRequestRefreshWorkingDocuments();
        break;
      case DOCUMENT_TAB_PANEL.templates:
        onRequestRefreshTemplates();
        onRequestRefreshWorkingDocuments();
        break;
      case DOCUMENT_TAB_PANEL.checkInNew:
        requestRefreshCheckInNewList();
        break;
      default:
        break;
    }
  }, [
    location.pathname,
    disableDocumentAccordions,
    onRequestRefreshWorkingDocuments,
    onRequestRefreshAllDocuments,
    onRequestRefreshTemplates,
    requestRefreshCheckInNewList,
    location.key,
    location.state,
  ]);

  // Collapse panels while channel connection warning is active (tabs disabled).
  React.useEffect(() => {
    if (disableDocumentAccordions) setExpandedPanel(false);
  }, [disableDocumentAccordions]);

  const [languageCodes, setLanguageCodes] = React.useState([]);
  const [languageCodesLoading, setLanguageCodesLoading] = React.useState(false);
  const [selectedLanguage, setSelectedLanguage] = React.useState(
    TEMPLATE_DEFAULT_LANGUAGE_OPTION
  );
  const languageCodesFetchedRef = React.useRef(false);
  const languageCodesFetchStartedRef = React.useRef(false);
  
  const [templates, setTemplates] = React.useState([]);
  const [templatesLoading, setTemplatesLoading] = React.useState(false);
  /** Cached templates by language code (incl. "ALL"); avoids refetch on accordion reopen. */
  const templatesCacheRef = React.useRef(new Map());
  const templatesPanelWasOpenRef = React.useRef(false);

  const fetchLanguageCodes = React.useCallback(async () => {
    if (languageCodesFetchedRef.current || languageCodesFetchStartedRef.current) {
      return;
    }
    languageCodesFetchStartedRef.current = true;

    try {
      setLanguageCodesLoading(true);
      const { data } = await getLanguageCode();
      setLanguageCodes(Array.isArray(data) ? data : []);
      languageCodesFetchedRef.current = true;
    } catch (err) {
      console.error("[Templates] get-language-code failed", err);
      setLanguageCodes([]);
    } finally {
      setLanguageCodesLoading(false);
    }
  }, []);

  const fetchTemplates = React.useCallback(
    async (
      languageCode = "",
      languageCodeList = languageCodes,
      { force = false } = {}
    ) => {
      const cacheKey = String(languageCode ?? "").toUpperCase();

      if (!force && templatesCacheRef.current.has(cacheKey)) {
        setTemplates(templatesCacheRef.current.get(cacheKey));
        return;
      }

      try {
        setTemplatesLoading(true);
        let templatesArray = [];

        if (isAllLanguagesSelection(languageCode)) {
          const allResponse = await getTemplatesByFileTypeAndLanguageCode(
            TEMPLATE_LANGUAGE_ALL_OPTION.value
          );
          templatesArray = normalizeTemplatesApiData(allResponse);

          if (templatesArray.length === 0) {
            const codes = getLanguageCodesFromList(languageCodeList);
            if (codes.length > 0) {
              const responses = await Promise.all(
                codes.map((code) =>
                  getTemplatesByFileTypeAndLanguageCode(code).catch(
                    () => ({ data: [], status: 204 })
                  )
                )
              );
              templatesArray = mergeTemplatesUnique(
                responses.flatMap((response) => normalizeTemplatesApiData(response))
              );
            }
          }
        } else {
          const response = await getTemplatesByFileTypeAndLanguageCode(languageCode);
          templatesArray = normalizeTemplatesApiData(response);
        }

        templatesCacheRef.current.set(cacheKey, templatesArray);
        setTemplates(templatesArray);
      } catch (err) {
        console.error("[Templates] get-templates-by-language-code FAILED", err);
        setTemplates([]);
      } finally {
        setTemplatesLoading(false);
      }
    },
    [languageCodes]
  );

  React.useEffect(() => {
    const isTemplatesPanel = expandedPanel === DOCUMENT_TAB_PANEL.templates;
    if (!isTemplatesPanel) return;
    if (disableDocumentAccordions) return;
    fetchLanguageCodes();
  }, [expandedPanel, disableDocumentAccordions, fetchLanguageCodes]);

  // Prefer the API label for English once language codes are loaded (value stays "en").
  React.useEffect(() => {
    if (!languageCodes.length) return;

    setSelectedLanguage((current) => {
      if (current?.value !== TEMPLATE_DEFAULT_LANGUAGE_OPTION.value) {
        return current;
      }

      const englishFromApi = languageCodes
        .map((item) => {
          const raw = item?.language ?? item?.Language ?? "";
          if (!raw) return null;
          const trimmed = String(raw).trim();
          const [codePart] = trimmed.split(" - ");
          const code = (codePart || trimmed).trim();
          return { value: code, label: trimmed };
        })
        .find(
          (opt) =>
            opt &&
            String(opt.value).toLowerCase() ===
              TEMPLATE_DEFAULT_LANGUAGE_OPTION.value
        );

      if (englishFromApi && englishFromApi.label !== current?.label) {
        return englishFromApi;
      }
      return current;
    });
  }, [languageCodes]);

  const handleLanguageChange = React.useCallback(
    (option) => {
      setSelectedLanguage(option);
      if (!option?.value) return;

      const force = shouldAlwaysRefreshTemplatesSelection(option.value);
      fetchTemplates(option.value, languageCodes, { force });
    },
    [fetchTemplates, languageCodes]
  );

  const uploadContext = React.useMemo(
    () =>
      requestTenantId != null
        ? {
            tenantId: requestTenantId,
            teamId: effectiveGroupId ?? "",
            channelId: effectiveChannelId ?? "",
            oneDriveRoot: effectiveChannelId ? "ChannelDoc" : "MainMenu",
          }
        : null,
    [requestTenantId, effectiveGroupId, effectiveChannelId]
  );

  const performTemplateDownload = React.useCallback(
    async (template) => {
      try {
        const result = await uploadTemplateToOneDrive(
          template,
          uploadContext ?? undefined
        ).unwrap();
        if (!result?.success) return;
        closeDownloadSuccessDialog();
        const documentTitle = template?.Title || "Document";
        dispatch({
          type: ON_SUCCESS_MESSAGE_INVOKE,
          payload: {
            type: SUCCESS_TYPE,
            title: "Download completed",
            message: `${documentTitle} downloaded successfully!`,
          },
        });

        if (result?.webUrl || result?.webDavUrl) {
          openDocumentPreferDesktop(
            { webUrl: result.webUrl, webDavUrl: result.webDavUrl },
            { logLabel: "TemplateDownload openDocumentPreferDesktop" },
          );
        }

        setExpandedPanel(DOCUMENT_TAB_PANEL.workingDocuments);
        const route = getRouteByDocumentPanel(DOCUMENT_TAB_PANEL.workingDocuments);
        if (location.pathname !== route) {
          navigate(route);
        }
        if (requestTenantId) {
          onWorkingDocumentLoad(
            effectiveGroupId,
            effectiveChannelId ?? "",
            requestTenantId,
            effectiveFileRoot,
            20
          );
        }
        onWorkingDocumentListStatusChange(DOCUMENT_LOAD_STATUS.onRefresh);
        onRefreshWorkingDocuments?.();
      } catch {
        // Errors are surfaced by uploadToOneDrive slice (dialog / message).
      }
    },
    [
      uploadTemplateToOneDrive,
      uploadContext,
      closeDownloadSuccessDialog,
      navigate,
      location.pathname,
      requestTenantId,
      effectiveGroupId,
      effectiveChannelId,
      effectiveFileRoot,
      onWorkingDocumentLoad,
      onWorkingDocumentListStatusChange,
      onRefreshWorkingDocuments,
      dispatch,
    ]
  );

  const handleTemplateDownload = React.useCallback(
    (template) => {
      setTemplateToDownload(template);
      if (isTemplateInOneDrive(template, workingDocumentList)) {
        setShowTemplateDownloadConfirm(true);
        return;
      }
      performTemplateDownload(template);
      setTemplateToDownload(null);
    },
    [workingDocumentList, performTemplateDownload]
  );

  const handleTemplateDownloadConfirm = React.useCallback(() => {
    if (templateToDownload) {
      performTemplateDownload(templateToDownload);
    }
    setShowTemplateDownloadConfirm(false);
    setTemplateToDownload(null);
  }, [templateToDownload, performTemplateDownload]);

  const handleTemplateDownloadToLocal = React.useCallback(
    async (template) => {
      if (!template) return;

      setIsTemplateLocalDownloadInProgress(true);
      try {
        const tokenResponse = await userAuth.getUserToken(true);
        const accessToken = tokenResponse?.accessToken ?? null;
        if (!accessToken) {
          dispatch({
            type: ON_ERROR_MESSAGE_INVOKE,
            payload: {
              type: ERROR_TYPE,
              message: "No access token. Sign in again and retry.",
            },
          });
          return;
        }

        const response = await getTemplatesDownload(accessToken, {
          docClass: template.DocClass,
          docNo: template.DocNo,
          docSheet: template.DocSheet,
          docRev: template.DocRev,
          formatSize: template.FormatSize,
          languageCode: template.LanguageCode,
          title: template.Title,
          uploadOneDrive: false,
          downloadMode: true,
        });

        const contentType =
          response?.headers?.["content-type"] ||
          response?.headers?.["Content-Type"] ||
          "application/octet-stream";
        const blob = decodeTemplateDownloadResponse(response?.data, contentType);
        if (!blob) {
          throw new Error("Template download payload is empty.");
        }

        const contentDisposition =
          response?.headers?.["content-disposition"] ||
          response?.headers?.["Content-Disposition"];
        const headerFileName = parseFileNameFromContentDisposition(contentDisposition);
        const payloadFileName =
          response?.data?.fileName ??
          response?.data?.FileName ??
          response?.data?.filename ??
          response?.data?.name ??
          "";
      
        const extension =
          normalizeTemplateExtension(template?.FileExtension) ||
          getFileNameExtension(headerFileName) ||
          getFileNameExtension(payloadFileName);
        const titleBase = String(
          template?.Title || template?.DocNo || "Template"
        ).trim();
        const fileName = extension ? `${titleBase}.${extension}` : titleBase;

        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = fileName;
        anchor.style.display = "none";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 120_000);

        dispatch({
          type: ON_SUCCESS_MESSAGE_INVOKE,
          payload: {
            type: SUCCESS_TYPE,
            title: "Download completed",
            message: `${template?.Title || "Template"} downloaded to local machine.`,
          },
        });
      } catch (err) {
        const responseMessage =
          err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.message ||
          "Template download failed.";
        dispatch({
          type: ON_ERROR_MESSAGE_INVOKE,
          payload: {
            type: ERROR_TYPE,
            message: responseMessage,
          },
        });
      } finally {
        setIsTemplateLocalDownloadInProgress(false);
      }
    },
    [dispatch]
  );

  // When the Templates panel opens, show cached data (no refetch unless cache is empty).
  React.useEffect(() => {
    const isTemplatesPanel = expandedPanel === DOCUMENT_TAB_PANEL.templates;

    if (!isTemplatesPanel) {
      templatesPanelWasOpenRef.current = false;
      return;
    }
    if (!selectedLanguage?.value) return;
    if (disableDocumentAccordions) return;
    if (languageCodesLoading) return;

    const panelJustOpened = !templatesPanelWasOpenRef.current;
    templatesPanelWasOpenRef.current = true;

    if (!panelJustOpened) return;

    fetchTemplates(selectedLanguage.value, languageCodes, { force: false });
  }, [
    expandedPanel,
    selectedLanguage?.value,
    disableDocumentAccordions,
    languageCodesLoading,
    fetchTemplates,
    languageCodes,
  ]);

  // "All" needs a second pass when language codes arrive after an empty cached result.
  React.useEffect(() => {
    if (selectedLanguage?.value !== TEMPLATE_LANGUAGE_ALL_OPTION.value) return;
    if (!languageCodes.length) return;
    if (disableDocumentAccordions) return;

    const cacheKey = TEMPLATE_LANGUAGE_ALL_OPTION.value;
    const cached = templatesCacheRef.current.get(cacheKey);
    if (cached?.length > 0) return;

    fetchTemplates(cacheKey, languageCodes, { force: true });
  }, [
    languageCodes,
    selectedLanguage?.value,
    disableDocumentAccordions,
    fetchTemplates,
  ]);

  const refreshDocumentPanel = React.useCallback(
    (panel) => {
      switch (panel) {
        case DOCUMENT_TAB_PANEL.workingDocuments:
          onRequestRefreshWorkingDocuments();
          break;
        case DOCUMENT_TAB_PANEL.allDocuments:
          onRequestRefreshAllDocuments();
          onRequestRefreshWorkingDocuments();
          break;
        case DOCUMENT_TAB_PANEL.templates:
          onRequestRefreshTemplates();
          break;
        case DOCUMENT_TAB_PANEL.checkInNew:
          requestRefreshCheckInNewList();
          break;
        default:
          break;
      }
    },
    [
      onRequestRefreshWorkingDocuments,
      onRequestRefreshAllDocuments,
      onRequestRefreshTemplates,
      requestRefreshCheckInNewList,
    ],
  );

  const handleChange = (panel) => (event, newExpanded) => {
    if (disableDocumentAccordions) return;

    const isCurrentlyExpanded = expandedPanel === panel;
    const clickedExpandIcon = isAccordionExpandIconClick(event);

    // Only the expand arrow may collapse an open panel; header clicks refresh instead.
    if (!newExpanded && isCurrentlyExpanded) {
      if (!clickedExpandIcon) {
        refreshDocumentPanel(panel);
        return;
      }
      setExpandedPanel(false);
      return;
    }

    setExpandedPanel(newExpanded ? panel : false);
    if (newExpanded) {
      const route = getRouteByDocumentPanel(panel);
      if (location.pathname !== route) {
        navigate(
          route,
          clickedExpandIcon
            ? {
                state: {
                  [NAV_LOCATION_STATE.skipDocumentPanelRouteRefresh]: true,
                },
              }
            : undefined
        );
      } else if (!clickedExpandIcon) {
        refreshDocumentPanel(panel);
      }
    }
  };

  // Refetch when sidebar/accordion requests refresh (same pattern as Working/All Documents).
  React.useEffect(() => {
    if (!templatesRefreshTrigger) return;
    if (!selectedLanguage?.value) return;
    if (disableDocumentAccordions) return;
    fetchTemplates(selectedLanguage.value, languageCodes, { force: true });
  }, [
    templatesRefreshTrigger,
    selectedLanguage?.value,
    disableDocumentAccordions,
    fetchTemplates,
    languageCodes,
  ]);

  /*
  const handleRefreshWorkingDocumentsClick = (event) => {
    event.stopPropagation();
    onWorkingDocumentListStatusChange(DOCUMENT_LOAD_STATUS.onRefresh);
    refreshWorkingDocuments(50);
  };

  const handleRefreshCheckInNewClick = (event) => {
    event.stopPropagation();
    requestRefreshCheckInNewList();
  };
  */

  return (
    <div
      style={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Accordion
        disabled={disableDocumentAccordions}
        expanded={expandedPanel === DOCUMENT_TAB_PANEL.workingDocuments}
        onChange={handleChange(DOCUMENT_TAB_PANEL.workingDocuments)}
        sx={buildAccordionShellSx(
          expandedPanel === DOCUMENT_TAB_PANEL.workingDocuments,
          theme
        )}
      >
        <AccordionSummary
          aria-controls="workingDocumentPanel-content"
          id="workingDocumentPanel-header"
          sx={{
            borderRadius: "20px",
            boxShadow: 2,
            "& .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root": {
              color: theme.colors.fontColor,
            },
            borderRadius: "10px",
            background:
              expandedPanel === DOCUMENT_TAB_PANEL.workingDocuments
                ? theme.colors.navSelectedStroke
                : theme.colors.themeColorBackground,
            display: "flex",
            "& .MuiAccordionSummary-content": {
              justifyContent: "space-between",
              alignItems: "center",
            },
            minHeight: 30,
            height: 40,
            "&.Mui-expanded": {
              minHeight: 30,
              height: 40,
            },
          }}
        >
          <AccordionHeaderLabel
            title="Working Documents"
            tooltip={connectedObjectsTooltipLines
              ? [
                  getDocumentPanelInfoTooltip(
                    teamsContext,
                    DOCUMENT_TAB_PANEL.workingDocuments
                  ),
                  ...connectedObjectsTooltipLines,
                ]
              : getDocumentPanelInfoTooltip(
                  teamsContext,
                  DOCUMENT_TAB_PANEL.workingDocuments
                )
            }
            theme={theme}
            isSelected={expandedPanel === DOCUMENT_TAB_PANEL.workingDocuments}
            sx={{ marginRight: 1 }}
          />
          {/*
          <AccordionHeaderRefreshButton
            theme={theme}
            isDarkMode={isDarkMode}
            isSelected={expandedPanel === DOCUMENT_TAB_PANEL.workingDocuments}
            disabled={isListLoading || disableDocumentAccordions}
            onClick={handleRefreshWorkingDocumentsClick}
          />
          */}
        </AccordionSummary>
        <AccordionDetails
          style={buildAccordionPanelDetailsStyle(workingDocumentsPanelBackground)}
        >
          <WorkingDocuments
            isRefreshing={false}
            onRefresh={onRefreshWorkingDocuments}
            isPanelActive={expandedPanel === DOCUMENT_TAB_PANEL.workingDocuments}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion
        disabled={disableDocumentAccordions}
        expanded={expandedPanel === DOCUMENT_TAB_PANEL.allDocuments}
        onChange={handleChange(DOCUMENT_TAB_PANEL.allDocuments)}
        slotProps={{ transition: { unmountOnExit: false } }}
        sx={buildAccordionShellSx(
          expandedPanel === DOCUMENT_TAB_PANEL.allDocuments,
          theme,
          "0em 0.25em 0.25em 0.25em"
        )}
      >
        <AccordionSummary
          aria-controls="allDocumentPanel-content"
          id="allDocumentPanel-header"
          sx={{
            borderRadius: "20px",
            boxShadow: 2,
            "& .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root": {
              color: theme.colors.fontColor,
            },
            borderRadius: "10px",
            background:
              expandedPanel === DOCUMENT_TAB_PANEL.allDocuments
                ? theme.colors.navSelectedStroke
                : theme.colors.themeColorBackground,
            display: "flex",
            "& .MuiAccordionSummary-content": {
              justifyContent: "space-between",
              alignItems: "center",
            },
            minHeight: 30,
            height: 40,
            "&.Mui-expanded": {
              minHeight: 30,
              height: 40,
            },
          }}
        >
          <AccordionHeaderLabel
            title="All Documents"
            tooltip={connectedObjectsTooltipLines
              ? [
                  getDocumentPanelInfoTooltip(
                    teamsContext,
                    DOCUMENT_TAB_PANEL.allDocuments
                  ),
                  ...connectedObjectsTooltipLines,
                ]
              : getDocumentPanelInfoTooltip(
                  teamsContext,
                  DOCUMENT_TAB_PANEL.allDocuments
                )
            }
            theme={theme}
            isSelected={expandedPanel === DOCUMENT_TAB_PANEL.allDocuments}
          />
        </AccordionSummary>
        <AccordionDetails
          style={buildAccordionPanelDetailsStyle(workingDocumentsPanelBackground)}
        >
          {hasOpenedAllDocuments && (
            <DocumentsContent
              isPanelActive={expandedPanel === DOCUMENT_TAB_PANEL.allDocuments}
            />
          )}
        </AccordionDetails>
      </Accordion>
      <Accordion
        disabled={disableDocumentAccordions}
        expanded={expandedPanel === DOCUMENT_TAB_PANEL.templates}
        onChange={handleChange(DOCUMENT_TAB_PANEL.templates)}
        sx={buildAccordionShellSx(
          expandedPanel === DOCUMENT_TAB_PANEL.templates,
          theme,
          "0em 0.25em 0.25em 0.25em"
        )}
      >
        <AccordionSummary
          aria-controls="templatesPanel-content"
          id="templatesPanel-header"
          sx={{
            borderRadius: "20px",
            boxShadow: 2,
            "& .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root": {
              color: theme.colors.fontColor,
            },
            borderRadius: "10px",
            background:
              expandedPanel === DOCUMENT_TAB_PANEL.templates
                ? theme.colors.navSelectedStroke
                : theme.colors.themeColorBackground,
            display: "flex",
            "& .MuiAccordionSummary-content": {
              justifyContent: "space-between",
              alignItems: "center",
            },
            minHeight: 30,
            height: 40,
            "&.Mui-expanded": {
              minHeight: 30,
              height: 40,
            },
          }}
        >
          <AccordionHeaderLabel
            title="Templates"
            tooltip={getDocumentPanelInfoTooltip(
              teamsContext,
              DOCUMENT_TAB_PANEL.templates
            )}
            theme={theme}
            isSelected={expandedPanel === DOCUMENT_TAB_PANEL.templates}
          />
        </AccordionSummary>
        <AccordionDetails
          style={buildAccordionPanelDetailsStyle(workingDocumentsPanelBackground, {
            padding: "12px",
            boxSizing: "border-box",
          })}
        >
          <LanguageSelectionDropdown
            languageCodes={languageCodes}
            value={selectedLanguage}
            onChange={handleLanguageChange}
          />
          <div
            style={{
              marginTop: 12,
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TemplatesList
              templates={templates}
              loading={templatesLoading || languageCodesLoading}
              languageCode={selectedLanguage?.value ?? ""}
              onDownload={handleTemplateDownload}
              onDownloadToLocal={handleTemplateDownloadToLocal}
            />
          </div>
          <ReusableDialog
            isOpen={
              ((uploadState?.loading ?? false) || isTemplateLocalDownloadInProgress) &&
              expandedPanel === DOCUMENT_TAB_PANEL.templates
            }
            onClose={() => {}}
            title="Download"
            showHeader={false}
            variant="info"
            centerContent
            dialogMinHeight={40}
            bodyMinHeight={40}
          >
            <DocumentDetailsLoadingContent message="Downloading..." />
          </ReusableDialog>
        </AccordionDetails>
      </Accordion>
      <Accordion
        disabled={disableDocumentAccordions}
        expanded={expandedPanel === DOCUMENT_TAB_PANEL.checkInNew}
        onChange={handleChange(DOCUMENT_TAB_PANEL.checkInNew)}
        sx={buildAccordionShellSx(
          expandedPanel === DOCUMENT_TAB_PANEL.checkInNew,
          theme,
          "0em 0.25em 0.25em 0.25em"
        )}
      >
        <AccordionSummary
          aria-controls="checkInNewPanel-content"
          id="checkInNewPanel-header"
          sx={{
            borderRadius: "10px",
            boxShadow: 2,
            "& .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root": {
              color: theme.colors.fontColor,
            },
            background:
              expandedPanel === DOCUMENT_TAB_PANEL.checkInNew
                ? theme.colors.navSelectedStroke
                : theme.colors.themeColorBackground,
            display: "flex",
            "& .MuiAccordionSummary-content": {
              justifyContent: "space-between",
              alignItems: "center",
            },
            minHeight: 30,
            height: 40,
            "&.Mui-expanded": {
              minHeight: 30,
              height: 40,
            },
          }}
        >
          <AccordionHeaderLabel
            title={checkInDocumentsNavLabel}
            tooltip={getDocumentPanelInfoTooltip(
              teamsContext,
              DOCUMENT_TAB_PANEL.checkInNew
            )}
            theme={theme}
            isSelected={expandedPanel === DOCUMENT_TAB_PANEL.checkInNew}
          />
          {/*
          <AccordionHeaderRefreshButton
            theme={theme}
            isDarkMode={isDarkMode}
            isSelected={expandedPanel === DOCUMENT_TAB_PANEL.checkInNew}
            disabled={checkInNewListLoading || disableDocumentAccordions}
            onClick={handleRefreshCheckInNewClick}
          />
          */}
        </AccordionSummary>
        <AccordionDetails
          style={buildAccordionPanelDetailsStyle(workingDocumentsPanelBackground)}
        >
          {hasOpenedCheckInNew && (
            <CheckInNewListContent
              isPanelActive={expandedPanel === DOCUMENT_TAB_PANEL.checkInNew}
            />
          )}
        </AccordionDetails>
      </Accordion>

      <ConfirmModal
        isOpen={showTemplateDownloadConfirm}
        onConfirm={handleTemplateDownloadConfirm}
        onCancel={() => {
          setShowTemplateDownloadConfirm(false);
          setTemplateToDownload(null);
        }}
        title={OVERWRITE_DOCUMENT_CONFIRMATION_TITLE}
        subText={OVERWRITE_TEMPLATE_CONFIRMATION_MSG}
        confirmButtonText="Confirm"
        cancelButtonText="Cancel"
        theme={theme}
        type="confirm"
      />
    </div>
  );
};

export default IfsDocsAccordionContent;
